import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuStylingOption} from '@Components/contextual-menu/components/contextual-menu-styling-option';
import type {IconStates} from '@Components/contextual-menu/components/styling-options';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './media-slide-alignment-option.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useAppSelector} from '@/hooks';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {useGetKeyfromSlideshowItem} from '@Hooks/poster-editor/useGetKeyfromSlideshowItem';

enum MediaSlideHorizontalAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

enum MediaSlideVerticalAlignment {
  TOP = 'top',
  CENTER = 'center',
  BOTTOM = 'bottom',
}

interface MediaSlideAlignmentOptionProps {
  showLargeIcons?: boolean;
  text?: string;
}

export function MediaSlideAlignmentOption({showLargeIcons = false, text = window.i18next.t('pmwjs_alignment')}: MediaSlideAlignmentOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const width = useGetKeyfromSlideshowItem('width');
  const height = useGetKeyfromSlideshowItem('height');
  const verticalAlign = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''];

    if (item?.gitype === ITEM_TYPE.SLIDESHOW) {
      const itemCopy = item as SlideshowItemObject;
      const slideItem = itemCopy.slides.slidesHashMap[itemCopy.selectedSlideUID];

      if ('mediaSlide' in slideItem && 'verticalAlign' in slideItem.mediaSlide) {
        return slideItem.mediaSlide.verticalAlign;
      }
    }

    return undefined;
  });
  const horizontalAlign = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''];

    if (item?.gitype === ITEM_TYPE.SLIDESHOW) {
      const itemCopy = item as SlideshowItemObject;
      const slideItem = itemCopy.slides.slidesHashMap[itemCopy.selectedSlideUID];

      if ('mediaSlide' in slideItem && 'verticalAlign' in slideItem.mediaSlide) {
        return slideItem.mediaSlide.horizontalAlign;
      }
    }

    return undefined;
  });

  if (!activeItem || !activeSlide || !activeSlideUID) {
    throw new Error('Active item or active slide is not defined in media alignment option');
  }

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item, instead got ${activeItem.gitype}`);
  }

  if (!activeSlide?.isMediaSlide()) {
    throw new Error(`Expected active slide to be of type image or video, instead got ${activeSlide.gitype}`);
  }

  const getDataForVerticalAlign = (): IconStates[] => {
    return [
      {
        id: MediaSlideVerticalAlignment.TOP,
        icon: 'icon-vertical-top',
        isSelected: verticalAlign === MediaSlideVerticalAlignment.TOP,
        iconSize: showLargeIcons ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_16,
      },
      {
        id: MediaSlideVerticalAlignment.CENTER,
        icon: 'icon-v-align-center',
        isSelected: verticalAlign === MediaSlideVerticalAlignment.CENTER,
        iconSize: showLargeIcons ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_16,
      },
      {
        id: MediaSlideVerticalAlignment.BOTTOM,
        icon: 'icon-vertical-bottom',
        isSelected: verticalAlign === MediaSlideVerticalAlignment.BOTTOM,
        iconSize: showLargeIcons ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_16,
      },
    ];
  };

  const getDataForHorizontalAlignment = (): IconStates[] => {
    return [
      {
        id: MediaSlideHorizontalAlignment.LEFT,
        icon: 'icon-vertical-top',
        isSelected: horizontalAlign === MediaSlideHorizontalAlignment.LEFT,
        className: styles.rotate,
        iconSize: showLargeIcons ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_16,
      },
      {
        id: MediaSlideHorizontalAlignment.CENTER,
        icon: 'icon-v-align-center',
        isSelected: horizontalAlign === MediaSlideHorizontalAlignment.CENTER,
        className: styles.rotate,
        iconSize: showLargeIcons ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_16,
      },
      {
        id: MediaSlideHorizontalAlignment.RIGHT,
        icon: 'icon-vertical-bottom',
        isSelected: horizontalAlign === MediaSlideHorizontalAlignment.RIGHT,
        className: styles.rotate,
        iconSize: showLargeIcons ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_16,
      },
    ];
  };

  const getData = (): IconStates[] => {
    if (activeSlide.mediaSlide.hasHorizontalAlignmentAxis()) {
      return getDataForHorizontalAlignment();
    }

    return getDataForVerticalAlign();
  };

  const onMediaSlideAlignmentModified = (id: MediaSlideHorizontalAlignment | MediaSlideVerticalAlignment): void => {
    const updatedMediaSlideObject = activeSlide.mediaSlide.hasHorizontalAlignmentAxis()
      ? {
          horizontalAlign: id,
        }
      : {
          verticalAlign: id,
        };
    void activeItem.updateSlideFromObject(activeSlideUID, {
      mediaSlide: updatedMediaSlideObject,
    });
  };

  return <ContextualMenuStylingOption key={`${width}-${height}`} onClick={onMediaSlideAlignmentModified} id="media-slide-alignment-option" label={text} data={getData()} />;
}
